import { uniqBy } from 'lodash';
import { addDays, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  FORMAT_QT_DATE,
  FORMAT_WEEKDAY_DATE_MINI,
  getTimeRange,
} from 'utils/helpers/datetime';

export function formatWeekdayDateMini(date) {
  return format(date, FORMAT_WEEKDAY_DATE_MINI);
}

export function formatQtDate(date) {
  const { timezone } = ecom.craftConfig;

  return formatInTimeZone(date, timezone, FORMAT_QT_DATE);
}

export function getRouteIdBySegmentIds(segmentIds, segments) {
  const targetSegment = Object.values(segments)
    .filter((segment) => segmentIds.includes(segment.id))
    .find((segment) => segment.is_primary_segment);

  return targetSegment?.qt_route_id;
}

export function getPeriodList(firstDate, days = 6) {
  const result = {};
  result[formatWeekdayDateMini(firstDate)] = formatQtDate(firstDate);
  let i = 1;

  while (i <= days) {
    const futureDate = addDays(firstDate, i);
    result[formatWeekdayDateMini(futureDate)] = formatQtDate(futureDate);

    i++;
  }

  return result;
}

export const getNodesFromStops = (stops) => {
  const nodes = stops.reduce((accumulator, currentValue) => {
    accumulator.push({
      type: 'stop',
      id: `${currentValue.id}`,
      name: currentValue.name,
    });
    if (currentValue.duration) {
      accumulator.push({
        type: 'duration',
        id: `duration-${currentValue.id}`,
        duration: currentValue.duration,
      });
    }
    return accumulator;
  }, []);
  return nodes;
};

export const getStopInfoFromStops = ({ routeStops, durationStore }) =>
  routeStops.map((item, index) => {
    if (index === routeStops.length - 1) {
      return {
        id: item.id,
        name: item.name,
      };
    }
    const nextStopId = routeStops[index + 1].id;
    return {
      id: item.id,
      name: item.name,
      duration: durationStore[`${item.id}-${nextStopId}`],
    };
  });

export const getUniqTripArray = ({ rawData = [], stopIdsInOrder }) => {
  const tripIds = uniqBy(rawData, 'trip_id').map((item) => item.trip_id);

  const uniqTripArray = tripIds.reduce((accumulator, tripId) => {
    const tripServices = rawData
      .filter((item) => item.trip_id === tripId)
      .map((item) => ({
        id: item.id,
        fromRouteStopId: item.from_route_stop_id,
        toRouteStopId: item.to_route_stop_id,
        departureTime: item.departure_time,
        arrivalTime: item.arrives_at,
        seasonalities: item.seasonalities,
      }));

    tripServices.sort(
      (a, b) =>
        stopIdsInOrder.indexOf(a.toRouteStopId) -
        stopIdsInOrder.indexOf(b.toRouteStopId)
    );

    const oneTrip = tripServices.reduce((acc, item) => {
      const { fromRouteStopId, departureTime, id, seasonalities } = item;
      acc[fromRouteStopId] = {
        departure_time: departureTime,
        id,
        seasonalities,
      };
      return acc;
    }, {});

    const lastTripService = tripServices[tripServices.length - 1];
    oneTrip[lastTripService.toRouteStopId] = {
      departure_time: lastTripService.arrivalTime,
      isLast: true,
      id: `${lastTripService.id}-last`,
      seasonalities: lastTripService.seasonalities,
    };

    return accumulator.concat(oneTrip);
  }, []);

  return uniqTripArray;
};

export const getTimeTableData = ({ uniqTripArray, stopIdsInOrder }) =>
  stopIdsInOrder.reduce((acc, stopId) => {
    const arr = uniqTripArray.map((trip, index) => {
      const stopInfo = trip[stopId];
      return stopInfo || { id: index };
    });

    acc[stopId] = arr;
    return acc;
  }, {});

export const getDurationStore = ({ rawData = [], stopIdsInOrder }) => {
  const result = {};
  stopIdsInOrder.reduce((lastV, currentV) => {
    result[`${lastV}-${currentV}`] = 'unknown';
    return currentV;
  });
  rawData.some((item) => {
    const timeRange = getTimeRange(item.departure_time, item.arrives_at);
    result[`${item.from_route_stop_id}-${item.to_route_stop_id}`] = timeRange;

    const values = Object.values(result);
    return !values.includes('unknown');
  });
  return result;
};
